import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Grid,
  Card,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { ToastContainer } from "react-toastify";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import EmptyData from "../../components/EmptyData";
import { useSelector } from "react-redux";
import ImageModal from "components/ImageModal";
import apiClient from "../../Utils/apiClient";

// Legacy Firebase implementation kept for reference during the backend migration:
// const db = getDatabase();
// const assetsRef = ref(db, "totalassets");
// const snapshot = await get(assetsRef);

const AssetRegistration = () => {
  const getUserState = useSelector((state) => state?.data?.getUserState);
  const [allData, setAllData] = useState([]);
  const [searchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [onClickImageData, setOnClickImageData] = useState("");
  const [loading, setLoading] = useState(false);
  const userProfileCacheRef = useRef(new Map());

  const navigate = useNavigate();

  const fetchUserProfile = useCallback(async (userId) => {
    if (!userId) {
      return null;
    }

    if (userProfileCacheRef.current.has(userId)) {
      return userProfileCacheRef.current.get(userId);
    }

    try {
      const response = await apiClient.get(`/users/${userId}`);
      const profile = response?.data || null;
      userProfileCacheRef.current.set(userId, profile);
      return profile;
    } catch (error) {
      userProfileCacheRef.current.set(userId, null);
      return null;
    }
  }, []);

  const getData = useCallback(async () => {
    setLoading(true);

    try {
      const response = await apiClient.get("/help-track/totalassets");
      const totalAssets = Array.isArray(response?.data) ? response.data : [];

      const list = await Promise.all(
        totalAssets.map(async (asset) => { 
          const userId = asset?.userId || "";
          const userData = await fetchUserProfile(userId);

          return {
            ...asset,
            assetId: asset?.id || asset?._id || asset?.assetId || "",
            userId,
            userName: userData?.userName || userData?.fullName || userData?.name || "N/A",
            userPhone: userData?.userPhone || userData?.phone || "N/A",
            userEmail: userData?.userEmail || userData?.email || "N/A",
          };
        }),
      );

      setAllData(list);
    } catch (error) {
      setAllData([]);
    } finally {
      setLoading(false);
    }
  }, [fetchUserProfile]);

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (!user) {
      navigate("/authentication/sign-in");
      return;
    }
    getData();
  }, [getData, getUserState, navigate]);

  const filteredData = allData?.filter((post) => {
    const searchText = searchTerm.toLowerCase();
    return (
      (post.userName || "").toLowerCase().includes(searchText) ||
      (post.userPhone || "").toLowerCase().includes(searchText) ||
      (post.userEmail || "").toLowerCase().includes(searchText) ||
      (post.assetType || "").toLowerCase().includes(searchText) ||
      (post.brandModel || "").toLowerCase().includes(searchText) ||
      (post.plateNumber || "").toLowerCase().includes(searchText)
    );
  });

  const onClose = () => {
    setIsModalOpen(false);
  };

  return (
    <DashboardLayout>
      <MDBox pt={12} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                sx={{ textAlign: "center" }}
              >
                <ToastContainer />
                <MDTypography variant="h6" color="white">
                  Asset Registration Dashboard
                </MDTypography>
              </MDBox>

              {loading ? (
                <MDBox py={6} textAlign="center">
                  <MDTypography variant="body2" color="text">
                    Loading assets...
                  </MDTypography>
                </MDBox>
              ) : filteredData.length > 0 ? (
                <MDBox>
                  <TableContainer component={Paper}>
                    <Table sx={{ "& td, & th": { fontSize: "13px", padding: "8px" } }}>
                      <TableBody>
                        <TableRow>
                          {[
                            "S/N",
                            "Name",
                            "Phone",
                            "Email",
                            "Asset Type",
                            "Brand/Model",
                            "Plate Number",
                            "Color",
                            "Photo",
                            "Proof of Ownership",
                          ].map((label) => (
                            <TableCell key={label} align="center" sx={{ fontWeight: "bold" }}>
                              {label}
                            </TableCell>
                          ))}
                        </TableRow>

                        {filteredData.map((post, index) => (
                          <TableRow key={post.assetId} hover sx={{ cursor: "pointer" }}>
                            <TableCell align="center">{index + 1}</TableCell>
                            <TableCell align="center">{post.userName}</TableCell>
                            <TableCell align="center">{post.userPhone}</TableCell>
                            <TableCell align="center">{post.userEmail}</TableCell>
                            <TableCell align="center">{post.assetType || "N/A"}</TableCell>
                            <TableCell align="center">{post.brandModel || "N/A"}</TableCell>
                            <TableCell align="center">{post.plateNumber || "N/A"}</TableCell>
                            <TableCell align="center">{post.color || "N/A"}</TableCell>
                            <TableCell align="center">
                              {Array.isArray(post.vehicleImgs) && post.vehicleImgs.length > 0 ? (
                                <div
                                  style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "6px",
                                    justifyContent: "center",
                                  }}
                                >
                                  {post.vehicleImgs.map((img, i) => (
                                    <img
                                      key={i}
                                      src={img}
                                      alt={`Vehicle-${i}`}
                                      width={40}
                                      height={40}
                                      style={{
                                        borderRadius: 5,
                                        cursor: "pointer",
                                        objectFit: "cover",
                                        border: "1px solid #ddd",
                                      }}
                                      onClick={() => {
                                        setIsModalOpen(true);
                                        setOnClickImageData(img);
                                      }}
                                    />
                                  ))}
                                </div>
                              ) : (
                                "N/A"
                              )}
                            </TableCell>

                            <TableCell align="center">
                              {post.ownershipImg ? (
                                <img
                                  src={post.ownershipImg}
                                  alt="Proof"
                                  width={40}
                                  height={40}
                                  style={{ borderRadius: 5 }}
                                  onClick={() => {
                                    setIsModalOpen(true);
                                    setOnClickImageData(post.ownershipImg);
                                  }}
                                />
                              ) : (
                                "N/A"
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </MDBox>
              ) : (
                <EmptyData />
              )}
            </Card>
            <ImageModal
              onClickImageData={onClickImageData}
              onClose={onClose}
              isModalOpen={isModalOpen}
            />
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
};

export default AssetRegistration;
